import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Stethoscope,
  Users,
  PenSquare,
  CheckCircle2,
  CalendarCheck,
  Plus,
  RefreshCw,
  BarChart3,
  Building2,
  UserCog,
  Syringe,
  Package,
  Pill,
  CakeSlice,
} from 'lucide-react';
import {
  EPISODE_STATUS_LABELS,
  EPISODE_STATUS_VARIANTS,
  type DashboardCountItem,
  type DashboardItemUsage,
  type EpisodeStatus,
} from '@datazo/contracts';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePageTitle } from '@/shared/store/page';
import { formatLongDateEs } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { useDashboardStats } from './api';

const TODAY_ISO = new Date().toISOString().slice(0, 10);

const STATE_COLORS: Record<EpisodeStatus, string> = {
  abierto: '#94a3b8',
  consentimiento: '#3b82f6',
  anestesia: '#a855f7',
  gasto: '#f59e0b',
  cerrado: '#16a34a',
  anulado: '#dc2626',
};

/** Orden fijo de los rangos de edad (el backend puede devolverlos en cualquier orden). */
const AGE_ORDER = ['0-17', '18-39', '40-59', '60-74', '75+'];

export function DashboardPage(): JSX.Element {
  usePageTitle('Dashboard', `Resumen operativo · ${formatLongDateEs(TODAY_ISO)}`);

  const statsQuery = useDashboardStats();
  const stats = statsQuery.data;
  const isLoading = statsQuery.isLoading;

  const rangosEdad = useMemo(() => {
    const items = stats?.rangosEdad ?? [];
    return [...items].sort(
      (a, b) => AGE_ORDER.indexOf(a.label) - AGE_ORDER.indexOf(b.label),
    );
  }, [stats]);

  const handleRefresh = (): void => {
    void statsQuery.refetch();
  };

  return (
    <div className="flex flex-col gap-5 animate-fade-in-up">
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-lg p-7 px-8 flex items-center gap-7 text-white shadow-[0_12px_32px_rgba(58,163,147,0.25)] bg-gradient-to-br from-[#2f877a] via-[#3aa393] to-[#4fb6a1]">
        <div
          aria-hidden
          className="absolute -right-[120px] -bottom-[120px] w-[320px] h-[320px] rounded-full bg-white/[0.08]"
        />
        <div className="relative z-10 flex-1 min-w-0">
          <h2 className="m-0 text-[26px] font-extrabold tracking-tight mb-2.5">
            Reporte clínico del día
          </h2>
          <div className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-white/85">
            <span className="w-2 h-2 rounded-full bg-green-400 shadow-[0_0_0_4px_rgba(34,197,94,0.25)] animate-pulse" />
            Jornada en curso · {formatLongDateEs(TODAY_ISO)}
          </div>
        </div>
        <div className="relative z-10 flex gap-8 items-start">
          <div className="text-right">
            <div className="text-3xl font-extrabold leading-none">
              {stats?.procedimientosHoy ?? '—'}
            </div>
            <div className="text-[10.5px] font-bold uppercase tracking-widest opacity-85 mt-1.5">
              Procedimientos hoy
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-extrabold leading-none">{stats?.cerradosMes ?? '—'}</div>
            <div className="text-[10.5px] font-bold uppercase tracking-widest opacity-85 mt-1.5">
              Cerrados este mes
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="md"
          onClick={handleRefresh}
          disabled={statsQuery.isFetching}
          className="relative z-10 bg-white/15 border border-white/25 text-white hover:bg-white/25 hover:text-white"
        >
          <RefreshCw className={cn('w-4 h-4', statsQuery.isFetching && 'animate-spin')} /> Actualizar
        </Button>
      </div>

      {statsQuery.isError ? (
        <Card>
          <div className="p-8 text-center text-sm text-text-muted">
            No se pudieron cargar las estadísticas.{' '}
            <button onClick={handleRefresh} className="text-primary font-semibold underline">
              Reintentar
            </button>
          </div>
        </Card>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5">
            <KpiCard
              label="Procedimientos hoy"
              value={isLoading ? '…' : (stats?.procedimientosHoy ?? 0)}
              icon={<Stethoscope className="w-[18px] h-[18px]" />}
              accent="indigo"
            />
            <KpiCard
              label="Firmas pendientes"
              value={isLoading ? '…' : (stats?.firmasPendientes ?? 0)}
              icon={<PenSquare className="w-[18px] h-[18px]" />}
              accent="amber"
            />
            <KpiCard
              label="Pacientes activos"
              value={isLoading ? '…' : (stats?.pacientesActivos ?? 0)}
              icon={<Users className="w-[18px] h-[18px]" />}
              accent="blue"
            />
            <KpiCard
              label="Cerrados en el día"
              value={isLoading ? '…' : (stats?.cerradosHoy ?? 0)}
              icon={<CalendarCheck className="w-[18px] h-[18px]" />}
              accent="green"
            />
            <KpiCard
              label="Cerrados en el mes"
              value={isLoading ? '…' : (stats?.cerradosMes ?? 0)}
              icon={<CheckCircle2 className="w-[18px] h-[18px]" />}
              accent="green"
            />
          </div>

          {/* Procedimientos de hoy */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-primary" /> Procedimientos de hoy
                </CardTitle>
                <CardDescription>
                  {isLoading
                    ? 'Cargando…'
                    : `${stats?.procedimientosDeHoy.length ?? 0} ${
                        (stats?.procedimientosDeHoy.length ?? 0) === 1 ? 'episodio' : 'episodios'
                      } en agenda hoy`}
                </CardDescription>
              </div>
              <Button size="sm" asChild>
                <Link to="/procedimientos/nuevo">
                  <Plus className="w-4 h-4" /> Nuevo
                </Link>
              </Button>
            </CardHeader>
            <div className="px-3 py-1">
              {isLoading ? (
                <div className="p-6 space-y-3" aria-busy="true">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-12 rounded bg-surface-alt animate-pulse" />
                  ))}
                </div>
              ) : (stats?.procedimientosDeHoy.length ?? 0) === 0 ? (
                <div className="p-10 text-center flex flex-col items-center gap-2 text-text-muted">
                  <Stethoscope className="w-7 h-7 opacity-50" />
                  <p className="text-sm">No hay episodios agendados para hoy.</p>
                </div>
              ) : (
                stats?.procedimientosDeHoy.map((ep) => (
                  <Link
                    key={ep.id}
                    to={`/procedimientos/${ep.id}`}
                    className="flex items-center gap-3 px-2.5 py-3 border-b border-border last:border-b-0 hover:bg-surface-alt rounded transition-colors"
                  >
                    <div
                      className="w-1.5 h-10 rounded-full flex-shrink-0"
                      style={{ backgroundColor: STATE_COLORS[ep.estado] }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-text text-sm truncate">{ep.tipo}</div>
                      <div className="text-text-muted text-xs">
                        {ep.hora} · {ep.pacienteNombre} · <code className="font-mono">{ep.id}</code>
                      </div>
                    </div>
                    <Badge variant={EPISODE_STATUS_VARIANTS[ep.estado]}>
                      {EPISODE_STATUS_LABELS[ep.estado]}
                    </Badge>
                  </Link>
                ))
              )}
            </div>
          </Card>

          {/* Rankings / distribuciones */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <RankCard
              title="Top 10 procedimientos por tipo"
              icon={<BarChart3 className="w-4 h-4 text-primary" />}
              isLoading={isLoading}
              data={toBars(stats?.topTipos)}
            />
            <RankCard
              title="Rango de edad"
              description="Pacientes atendidos por grupo etario."
              icon={<CakeSlice className="w-4 h-4 text-primary" />}
              isLoading={isLoading}
              data={toBars(rangosEdad)}
            />
            <RankCard
              title="Entidad del procedimiento"
              description="Por entidad responsable del pago."
              icon={<Building2 className="w-4 h-4 text-primary" />}
              isLoading={isLoading}
              data={toBars(stats?.porEntidad)}
            />
            <RankCard
              title="Procedimientos por médico"
              icon={<UserCog className="w-4 h-4 text-primary" />}
              isLoading={isLoading}
              data={toBars(stats?.porMedico)}
            />
            <RankCard
              title="Procedimientos por anestesiólogo"
              icon={<Syringe className="w-4 h-4 text-primary" />}
              isLoading={isLoading}
              data={toBars(stats?.porAnestesiologo)}
            />
            <RankCard
              title="Top 10 insumos más utilizados"
              icon={<Package className="w-4 h-4 text-primary" />}
              isLoading={isLoading}
              data={toUsageBars(stats?.topInsumos)}
            />
            <RankCard
              title="Top 10 medicamentos más utilizados"
              icon={<Pill className="w-4 h-4 text-primary" />}
              isLoading={isLoading}
              data={toUsageBars(stats?.topMedicamentos)}
            />
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Bars                                                                        */
/* -------------------------------------------------------------------------- */

interface Bar {
  key: string;
  label: string;
  value: number;
}

function toBars(items: ReadonlyArray<DashboardCountItem> | undefined): Bar[] {
  return (items ?? []).map((it) => ({ key: it.label, label: it.label, value: it.count }));
}

function toUsageBars(items: ReadonlyArray<DashboardItemUsage> | undefined): Bar[] {
  return (items ?? []).map((it) => ({
    key: it.codigo,
    label: `${it.codigo} · ${it.nombre}`,
    value: it.cantidad,
  }));
}

interface RankCardProps {
  title: string;
  description?: string;
  icon: JSX.Element;
  isLoading: boolean;
  data: Bar[];
}

function RankCard({ title, description, icon, isLoading, data }: RankCardProps): JSX.Element {
  const max = data.reduce((acc, d) => Math.max(acc, d.value), 0);
  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            {icon} {title}
          </CardTitle>
          {description !== undefined && <CardDescription>{description}</CardDescription>}
        </div>
      </CardHeader>
      <div className="p-6">
        {isLoading ? (
          <div className="space-y-2.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-6 rounded bg-surface-alt animate-pulse" />
            ))}
          </div>
        ) : data.length === 0 ? (
          <div className="text-center text-sm text-text-muted py-8">Sin datos para mostrar.</div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {data.map((d) => {
              const pct = max === 0 ? 0 : (d.value / max) * 100;
              return (
                <div key={d.key} className="flex items-center gap-3 text-sm">
                  <div className="w-[170px] text-text-soft truncate" title={d.label}>
                    {d.label}
                  </div>
                  <div className="flex-1 h-7 bg-surface-alt rounded overflow-hidden relative">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-accent rounded transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                    <span className="absolute inset-0 flex items-center px-2 text-xs font-bold text-white mix-blend-luminosity tabular-nums">
                      {Number.isInteger(d.value) ? d.value : d.value.toFixed(1)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* KPI card                                                                    */
/* -------------------------------------------------------------------------- */

interface KpiCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  accent: 'indigo' | 'amber' | 'blue' | 'green';
}

const ACCENT_CLASS: Record<KpiCardProps['accent'], string> = {
  indigo: 'bg-primary-soft text-primary',
  amber: 'bg-warning-soft text-amber-600',
  blue: 'bg-info-soft text-info',
  green: 'bg-success-soft text-success',
};

function KpiCard({ label, value, icon, accent }: KpiCardProps): JSX.Element {
  return (
    <div className="bg-surface border border-border rounded-lg p-5 shadow-sm flex flex-col gap-1.5 min-h-[120px] hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2.5">
        <div className="text-text-muted text-[10.5px] font-bold uppercase tracking-widest">
          {label}
        </div>
        <div
          className={cn(
            'w-9 h-9 rounded-md grid place-items-center flex-shrink-0',
            ACCENT_CLASS[accent],
          )}
        >
          {icon}
        </div>
      </div>
      <div className="text-[32px] font-extrabold tracking-tight leading-tight text-text mt-1 tabular-nums">
        {value}
      </div>
    </div>
  );
}
